import type { Workbook } from 'exceljs';
import { formatHandle, listenerLabel } from '../../lib/listenerName';
import { STATUS_LABEL, type AdminPayoutRow, type PayoutActor } from './payoutViews';

/** `GET /admin/payouts/export` — every payout matching the History filters. */
export interface PayoutExport {
  items: AdminPayoutRow[];
  /** True when the filter matched more rows than one export holds. */
  truncated: boolean;
}

const RUPEE_FMT = '"₹"#,##0.00';
const DATE_FMT = 'dd mmm yyyy, h:mm AM/PM';

const toRupees = (paise: string) => Number(paise) / 100;
const actorName = (a: PayoutActor | null | undefined) => (a ? (a.name ?? a.email ?? 'Admin') : '');

/**
 * An instant as an Excel date in the viewer's own time zone. exceljs writes a
 * `Date` as UTC wall-clock, so the offset is folded in first — the cell then
 * shows the same local time the panel does.
 */
function toExcelDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
}

const COLUMNS: Array<{ header: string; width: number; numFmt?: string }> = [
  { header: 'Listener', width: 26 },
  { header: 'Handle', width: 20 },
  { header: 'Amount requested', width: 18, numFmt: RUPEE_FMT },
  { header: 'Tax deducted (TDS)', width: 18, numFmt: RUPEE_FMT },
  { header: 'Amount to pay', width: 16, numFmt: RUPEE_FMT },
  { header: 'Status', width: 20 },
  { header: 'Pay by', width: 10 },
  { header: 'Account holder', width: 24 },
  { header: 'Bank name', width: 22 },
  { header: 'Account number', width: 22 },
  { header: 'IFSC', width: 14 },
  { header: 'UPI ID', width: 26 },
  { header: 'Submitted', width: 22, numFmt: DATE_FMT },
  { header: 'Approved by', width: 20 },
  { header: 'Approved at', width: 22, numFmt: DATE_FMT },
  { header: 'Marked paid by', width: 20 },
  { header: 'Paid at', width: 22, numFmt: DATE_FMT },
  { header: 'Payment reference', width: 24 },
  { header: 'Rejected by', width: 20 },
  { header: 'Rejected at', width: 22, numFmt: DATE_FMT },
  { header: 'Rejection reason', width: 40 },
  { header: 'Payout ID', width: 38 },
];

/** One "Payouts" sheet: a row per payout request, with a totals row at the end. */
export async function buildPayoutWorkbook(rows: AdminPayoutRow[], subtitle: string): Promise<Workbook> {
  // exceljs is large — loaded only when someone exports.
  const mod = await import('exceljs');
  const ExcelJS = mod.default ?? mod;
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Dude admin';
  wb.created = new Date();

  const ws = wb.addWorksheet('Payouts', { views: [{ state: 'frozen', xSplit: 1, ySplit: 3 }] });
  ws.getCell('A1').value = 'Listener payouts';
  ws.getCell('A1').font = { bold: true, size: 14 };
  ws.getCell('A2').value = subtitle;
  ws.getRow(3).values = COLUMNS.map((c) => c.header);
  const header = ws.getRow(3);
  header.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  header.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD6336C' } };
    cell.alignment = { vertical: 'middle', wrapText: true };
  });
  COLUMNS.forEach((c, i) => {
    const col = ws.getColumn(i + 1);
    col.width = c.width;
    if (c.numFmt) col.numFmt = c.numFmt;
  });

  for (const r of rows) {
    const upi = r.payoutMethod === 'upi';
    const paid = r.status === 'settled';
    const rejected = r.status === 'rejected' || r.status === 'reversed';
    ws.addRow([
      listenerLabel({ displayName: r.listener.displayName, handle: r.listener.user.handle }),
      formatHandle(r.listener.user.handle) ?? '',
      toRupees(r.amountPaise),
      toRupees(r.tdsPaise),
      toRupees(r.netPayoutPaise),
      STATUS_LABEL[r.status] ?? r.status,
      upi ? 'UPI' : 'Bank',
      // Bank holder, or the UPI holder's name for a UPI payout.
      r.accountHolderName ?? '',
      upi ? '' : (r.bankName ?? ''),
      // Text, never a number: Excel would drop leading zeros and round long accounts.
      upi ? '' : (r.accountNumber ?? ''),
      upi ? '' : (r.ifsc ?? ''),
      upi ? (r.upiId ?? '') : '',
      toExcelDate(r.createdAt),
      actorName(r.approvedByAdmin),
      toExcelDate(r.approvedAt),
      paid ? actorName(r.paidByAdmin) : '',
      paid ? toExcelDate(r.settledAt) : null,
      r.paymentReference ?? '',
      rejected ? actorName(r.rejectedByAdmin) : '',
      rejected ? toExcelDate(r.rejectedAt) : null,
      rejected ? (r.reversalReason ?? '') : '',
      r.id,
    ]);
  }

  if (rows.length > 0) {
    const last = ws.rowCount;
    const total = ws.addRow([`Total (${rows.length} payouts)`]);
    total.font = { bold: true };
    for (const col of ['C', 'D', 'E']) {
      total.getCell(col).value = { formula: `SUM(${col}4:${col}${last})` };
    }
  } else {
    ws.addRow(['No payouts match this filter']);
  }
  return wb;
}

/** Builds the workbook and hands it to the browser as an .xlsx download. */
export async function downloadPayoutWorkbook(rows: AdminPayoutRow[], subtitle: string, fileName: string): Promise<void> {
  const wb = await buildPayoutWorkbook(rows, subtitle);
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}
